import { FunctionComponent, memo, useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { UserContext } from '../../../../../context';
import {
  storyCardApi,
  StoryCardTemplate,
  StoryCardTemplateSettings,
  StoryCardTemplateStoryCardInput,
  useStoriesByChannelIdQuery,
} from '../../../../../services';
import {
  Button,
  ButtonType,
  CreateCardModal,
  getPostFieldsFromCard,
  IconLabel,
  InputField,
  isEmptyString,
  Modal,
  Select,
  Switch,
  useIsNarrowViewport,
} from '../../../../../shared';

import classNames from 'classnames';
import classes from '../../Category/CreateCategoryModal/CreateCategoryModal.module.scss';

interface CardTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  submitCallback: () => void;
  template?: StoryCardTemplate;
}

export const CardTemplateModal: FunctionComponent<CardTemplateModalProps> = memo(
  ({ isOpen, onClose, submitCallback, template }) => {
    const { t } = useTranslation();

    const { t: ctxT } = useTranslation('', { keyPrefix: 'storyCardCategory' });

    const { channelId } = useContext(UserContext).userInfo.userData;

    const { data: stories = [], isFetching } = useStoriesByChannelIdQuery({
      channelId,
      filter: { onlyPostIn: true },
    });

    const [storyCardTemplateCreate] = storyCardApi.endpoints.storyCardTemplateCreate.useMutation();

    const [storyCardTemplateUpdate] = storyCardApi.endpoints.storyCardTemplateUpdate.useMutation();

    const isNarrowViewport = useIsNarrowViewport();

    const [name, setName] = useState<string>(template?.name ?? '');

    const [settings, setSettings] = useState<StoryCardTemplateSettings>(
      template?.settings ?? {
        buttonTextEn: '',
        buttonTextDe: '',
        showAsOption: true,
        showAsButton: false,
      }
    );

    const [storyId, setStoryId] = useState<number | undefined>(template?.story?.id);

    const [isCreateCardModalOpen, setIsCreateCardModalOpen] = useState<boolean>(false);

    const [isSaving, setIsSaving] = useState<boolean>(false);

    const { buttonTextEn, buttonTextDe, showAsOption, showAsButton } = settings;

    const title = t(`storyCardCategoryTemplate.${template ? 'edit' : 'create'}`);

    const postInStories = useMemo(
      () => stories.filter(({ id }) => id === storyId),
      [stories, storyId]
    );

    const postFilledFields = useMemo(
      () => getPostFieldsFromCard(template?.storyCard),
      [template?.storyCard]
    );

    const saveHandler = useCallback(
      async (storyCard: StoryCardTemplateStoryCardInput) => {
        setIsSaving(true);

        try {
          const { payload } = await (template
            ? storyCardTemplateUpdate({
                storyCardId: template.id,
                name,
                settings,
                storyId: storyId ?? null,
                storyCard,
              })
            : storyCardTemplateCreate({ name, settings, storyId, storyCard })
          ).unwrap();

          if (!payload) {
            toast.error(t('common.error-message'));
            return;
          }

          onClose();
          submitCallback();
        } catch (_) {
          toast.error(t('common.error-message'));
        } finally {
          setIsSaving(false);
        }
      },
      [
        name,
        onClose,
        settings,
        storyCardTemplateCreate,
        storyCardTemplateUpdate,
        storyId,
        submitCallback,
        t,
        template,
      ]
    );

    const body = useMemo(() => {
      return (
        <div
          className={classNames(classes['category'], {
            [classes['category--narrow']]: isNarrowViewport,
          })}
        >
          <div className={classes['category__title']}>{title}</div>

          <div className={classes['category__scrollable']}>
            <InputField
              value={name}
              placeholder={t('storyCardTemplates.name')}
              maxLength={100}
              onChange={({ target }) => setName(target.value)}
            />

            <div className={classes['category__row']}>
              <InputField
                value={buttonTextEn}
                placeholder={ctxT('button-placeholder-US')}
                onChange={({ target }) => setSettings({ ...settings, buttonTextEn: target.value })}
                icon={<IconLabel iconId={'lang-EN'} iconSize={18} singleColor nonClickable />}
              />
              <InputField
                value={buttonTextDe}
                placeholder={ctxT('button-placeholder-DE')}
                onChange={({ target }) => setSettings({ ...settings, buttonTextDe: target.value })}
                icon={<IconLabel iconId={'lang-DE'} iconSize={18} singleColor nonClickable />}
              />
            </div>

            <Select
              label={t('common.story')}
              selectedItemId={storyId}
              items={stories}
              disabled={isFetching}
              listMaxHeightRem={12.5}
              onChange={setStoryId}
              staticListPosition
              search
              uncheck
            />

            <div className={classes['category__row']}>
              <Switch
                label={ctxT('show-as-option')}
                className={classes['category__row-switch']}
                checked={showAsOption}
                onChange={(showAsOption) => setSettings({ ...settings, showAsOption })}
              />
              <Switch
                label={ctxT('show-as-button')}
                className={classes['category__row-switch']}
                checked={showAsButton}
                onChange={(showAsButton) => setSettings({ ...settings, showAsButton })}
              />
            </div>
          </div>

          <Button
            className={classes['category__button']}
            type={ButtonType.primary}
            label={t('common.next')}
            disabled={[name, buttonTextEn, buttonTextDe].some(isEmptyString)}
            onClick={() => setIsCreateCardModalOpen(true)}
          />
        </div>
      );
    }, [
      buttonTextDe,
      buttonTextEn,
      ctxT,
      isFetching,
      isNarrowViewport,
      name,
      settings,
      showAsButton,
      showAsOption,
      stories,
      storyId,
      t,
      title,
    ]);

    return (
      <>
        <Modal isOpen={isOpen} body={body} onClose={onClose} ariaLabel={title} alignTop />

        {isCreateCardModalOpen && (
          <CreateCardModal
            {...postFilledFields}
            isOpen={isCreateCardModalOpen}
            onClose={() => setIsCreateCardModalOpen(false)}
            postInStories={postInStories}
            card={template?.storyCard}
            onTemplateSave={saveHandler}
            isTemplateSaving={isSaving}
          />
        )}
      </>
    );
  }
);
